import { Pog } from "@CommonTypes/merchflow/pog/pog";
import { PrimitiveAtom, useAtom } from "jotai";
import { sideViewAtom } from "../../store/atoms";
import { Bay } from "../Bay/Bay";
import { Flex, VerticalDivider } from "src/elements";
import { useEffect, useMemo } from "react";
import { PlanogramBay } from "@CommonTypes/merchflow/pog/bay";
import { MergedShelf } from "../Shelf/MergedShelf";
import { PlanogramItemNonCircular } from "@CommonTypes/merchflow/pog/item";
import { projectPlanogramItemForSideView } from "./sideViewProjection";
import { noop } from "es-toolkit";
import { hasShelfLoader } from "@CommonUtils/pog/shelfLoader";

interface Props {
  pogAtom: PrimitiveAtom<Pog | null>;
  scaleX: number | null;
  scaleY: number | null;
}

export const PlanogramSideView = ({ pogAtom, scaleX, scaleY }: Props) => {
  const [pog] = useAtom(pogAtom);
  const [sideView, setSideView] = useAtom(sideViewAtom);

  useEffect(() => {
    return () => {
      setSideView(null);
    };
  }, []);

  if (!pog || !sideView || !scaleX || !scaleY) return null;

  const sideBay = useMemo(() => {
    const originalBay = pog.getBay(sideView.bayUniqueId);
    if (!originalBay) return null;

    const sideBay = new PlanogramBay(
      {
        uniqueId: originalBay.uniqueId,
        width: originalBay.depth,
        height: originalBay.height,
        depth: originalBay.width,
        baseHeight: originalBay.baseHeight,
        notchOffset: originalBay.notchOffset,
        notchSpacing: originalBay.notchSpacing,
        maxNotch: originalBay.maxNotch,
        maxShelfHeight: originalBay.maxShelfHeight,
        maxProductHead: originalBay.maxProductHead,
        shelves: originalBay.shelves.map((originalShelf) => {
          const mergedShelves = originalShelf.getMergedShelves();

          return {
            uniqueId: originalShelf.uniqueId,
            width: originalShelf.depth,
            depth: originalShelf.width,
            thickness: originalShelf.thickness,
            offset: originalShelf.offset,
            grillHeight: originalShelf.grillHeight,
            type: originalShelf.type,
            variantRestrictions: originalShelf.variantRestrictions,
            notchNo: originalShelf.getNotchNo(),
            mergedLeft: null,
            mergedRight: null,
            items: mergedShelves.reduce<PlanogramItemNonCircular[]>(
              (sideViewItems, mergedShelf) => {
                if (sideViewItems.length === 0) {
                  for (const originalItem of mergedShelf.items) {
                    const currentItemCoordinates = originalItem.getCoordinates();

                    const xOverlap =
                      Math.max(currentItemCoordinates.xLeft, sideView.itemXLeft) <
                      Math.min(currentItemCoordinates.xRight, sideView.itemXRight);

                    if (xOverlap) {
                      sideViewItems.push(
                        projectPlanogramItemForSideView(originalItem, originalShelf.uniqueId),
                      );
                      break;
                    }
                  }
                }

                return sideViewItems;
              },
              [],
            ),
            bay: originalBay.uniqueId,
          };
        }),
        pegboards: [],
      },
      pog.planogram,
    );

    return sideBay;
  }, [pog, sideView]);

  // The side view rebuilds shelves without their loader; carry the depth across to draw the band.
  const originalBay = pog.getBay(sideView.bayUniqueId);
  const sideViewLoaderDepths = new Map(
    (originalBay?.shelves ?? []).map((shelf) => [
      shelf.uniqueId,
      hasShelfLoader(shelf) ? (shelf.loaderDepth ?? 0) : 0,
    ]),
  );

  if (!sideBay) return null;

  return (
    <Flex padding="0 20px 0 0" gap="20px">
      <Bay
        bay={sideBay}
        isEditable
        pogAtom={pogAtom as PrimitiveAtom<Pog>}
        scaleX={scaleX}
        scaleY={scaleY}
      >
        {sideBay.shelves.map((shelf) => (
          <MergedShelf
            key={shelf.uniqueId}
            bay={sideBay}
            shelves={[shelf]}
            bounds={{
              xLeft: 0,
              xRight: shelf.width,
              yBottom: shelf.getYBottom(),
              yTop: shelf.getYTop(),
            }}
            scaleX={scaleX}
            scaleY={scaleY}
            isEditable
            isPog={true}
            highlightedItems={[]}
            pogAtom={pogAtom as PrimitiveAtom<Pog>}
            setIsModified={noop}
            availableStores={null}
            pdfMode={false}
            isSideView
            sideViewLoaderDepth={sideViewLoaderDepths.get(shelf.uniqueId) ?? 0}
          />
        ))}
      </Bay>

      <VerticalDivider />
    </Flex>
  );
};
